import { compact, times } from 'lodash'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Item, createAvailabilitySummary } from '../../models/Item'
import { Lesson } from '../../models/Lesson'
import { Transaction } from '../../models/Transaction'
import { timeComponentsFromDecimalTime } from '../../utils/dates'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import SegmentedControl from '../app/design/SegmentedControl'
import { CalendarColorSelect } from '../calendar/CalendarColorSelect'
import { SelectDaysOfTheWeek } from '../common/SelectDaysOfTheWeek'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { DatePicker } from '../common/fields/DatePicker'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import {
    durationDayOptions as durationDayOptionsWithTBD,
    fiveMinuteIncrementOptions,
} from '../settings/Options'
import {
    SetupBusinessHoursModal,
    Weekday,
    createAvailabilityArray,
} from '../settings/SetupBusinessHoursModal'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { ItemLocationChips } from './ItemLocationChips'
import { ItemLocationModal } from './ItemLocationModal'
import { TypeSearchableSelect } from './TypeSearchableSelect'

type Props = {
    item: Item
    onClose: () => void
    onDelete?: () => void
    isLoading?: boolean
    onSave: (item: Item) => void
    enableAllFields?: boolean
}

const capacityOptions = Item.capacityStrings(1, Lesson.CLASS_CAPACITY)
const durationDayOptions = (() => {
    const [_, ...options] = durationDayOptionsWithTBD
    return [{ label: 'Flexible', value: '' }, ...options]
})()

export const ReservationPage = ({
    item,
    onClose,
    onDelete,
    isLoading,
    onSave,
    enableAllFields = true,
}: Props) => {
    const [isSaving, setIsSaving] = useState(false)
    const [scratchReservation, setScratchReservation] = useState<Item>(item)
    const owner = PSApplication.owner()
    const [guardrail, setGuardrail] = useState<
        undefined | 'singleCapacity' | 'availability' | 'location' | 'price'
    >(undefined)

    const [activeModal, setActiveModal] = useState<
        'check-in' | 'check-in-availability-hours' | 'check-out' | null
    >(null)

    const [uploadedImage, setUploadedImage] = useState<
        { type: 'file'; file: File } | { type: 'url'; url: string } | null
    >(item.image ? { type: 'url', url: item.image.url ?? '' } : null)

    const [hasFixedStartTime, setHasFixedStartTime] = useState(
        scratchReservation.startDate !== undefined
    )

    const handleAvailabilityChange = (weekdays: Weekday[]) => {
        const newReservation = scratchReservation.clone({ schedule: 0 })
        for (let i = 0; i < weekdays.length; i++) {
            const start = weekdays[i].start
            const end = weekdays[i].end
            const day = (['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const)[i]

            if (weekdays[i].enabled === false) {
                newReservation.setDayStartHour(day, undefined)
                newReservation.setDayEndHour(day, undefined)
            }
            else {
                newReservation.schedule! += 1 << i
                if (start) newReservation.setDayStartHour(day, start.value)
                if (end) newReservation.setDayEndHour(day, end.value)
            }
        }

        setScratchReservation(newReservation)
    }

    const isNextEnabled =
        scratchReservation.name &&
        scratchReservation.capacity &&
        !!scratchReservation.checkinType &&
        !!scratchReservation.checkoutType

    const [checkinTimeHour, checkinTimeMinute, checkinTimeAMPM] = timeComponentsFromDecimalTime(
        scratchReservation.checkinTime ?? 9
    )
    const [checkoutTimeHour, checkoutTimeMinute, checkoutTimeAMPM] = timeComponentsFromDecimalTime(
        scratchReservation.checkoutTime ?? 9
    )

    const availability = createAvailabilityArray(scratchReservation)

    const [checkinAvailabilityType, setCheckinAvailabilityType] = useState<
        'Same as business' | 'Custom schedule'
    >(availability.some((weekday) => weekday.enabled) ? 'Custom schedule' : 'Same as business')

    const isCheckoutAvailabilitySameAsBusiness =
        (scratchReservation.checkoutType === 'flex' || scratchReservation.checkoutType === 'any') &&
        scratchReservation.checkoutStartTime === null &&
        scratchReservation.checkoutEndTime === null

    const checkinDescription = useMemo(() => {
        if (scratchReservation.checkinType === null) {
            return undefined
        }

        if (scratchReservation.checkinType === 'flex') {
            const durationDescription =
                scratchReservation.checkinDuration !== null &&
                scratchReservation.checkinDuration !== 0
                    ? (fiveMinuteIncrementOptions.find(
                          (option) =>
                              option.value === scratchReservation.checkinDuration?.toString()
                      )?.label ?? null)
                    : null

            return compact([
                'Flexible check-in',
                durationDescription,
                createAvailabilitySummary(availability, 'Same as business'),
            ]).join(', ')
        }
        else if (scratchReservation.checkinType === 'any') {
            return ['Any time', createAvailabilitySummary(availability, 'Same as business')].join(
                ', '
            )
        }
        else {
            return [
                'Default check-in time',
                `${checkinTimeHour === 0 ? 12 : checkinTimeHour}:${checkinTimeMinute.toString().padStart(2, '0')} ${checkinTimeAMPM}`,
                createAvailabilitySummary(availability, 'Same as business'),
            ].join(', ')
        }
    }, [scratchReservation, availability, checkinTimeHour, checkinTimeMinute, checkinTimeAMPM])

    const checkoutDescription = useMemo(() => {
        if (scratchReservation.checkoutType === null) {
            return undefined
        }

        const timeDescription = (() => {
            if (!scratchReservation.checkoutStartTime || !scratchReservation.checkoutEndTime) {
                return null
            }

            const [checkoutStartTimeHour, checkoutStartTimeMinute, checkoutStartTimeAMPM] =
                timeComponentsFromDecimalTime(scratchReservation.checkoutStartTime)
            const [checkoutEndTimeHour, checkoutEndTimeMinute, checkoutEndTimeAMPM] =
                timeComponentsFromDecimalTime(scratchReservation.checkoutEndTime)

            return `${checkoutStartTimeHour}:${checkoutStartTimeMinute.toString().padStart(2, '0')} ${checkoutStartTimeAMPM} - ${checkoutEndTimeHour}:${checkoutEndTimeMinute.toString().padStart(2, '0')} ${checkoutEndTimeAMPM}`
        })()

        if (scratchReservation.checkoutType === 'flex') {
            const durationDescription =
                scratchReservation.checkoutDuration !== null &&
                scratchReservation.checkoutDuration !== 0
                    ? (fiveMinuteIncrementOptions.find(
                          (option) =>
                              option.value === scratchReservation.checkoutDuration?.toString()
                      )?.label ?? null)
                    : null

            return compact([
                'Flexible check-out',
                durationDescription,
                timeDescription ?? 'Same as business',
            ]).join(', ')
        }
        else if (scratchReservation.checkoutType === 'any') {
            return compact(['Any time', timeDescription ?? 'Same as business']).join(', ')
        }
        else {
            return [
                'Default check-out time',
                `${checkoutTimeHour === 0 ? 12 : checkoutTimeHour}:${checkoutTimeMinute.toString().padStart(2, '0')} ${checkoutTimeAMPM}`,
            ].join(', ')
        }
    }, [scratchReservation])

    return (
        <WorkflowPage
            title="Reservation"
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        onDelete !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {onDelete !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={onDelete}
                            isLoading={isLoading}
                        >
                            Delete reservation
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={() => {
                            setIsSaving(true)
                            onSave(scratchReservation)
                        }}
                        disabled={isLoading || !isNextEnabled}
                        isLoading={isSaving}
                    >
                        Save
                    </Button>
                </div>
            }
            onClose={onClose}
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    <WorkflowPage.PhotoPicker
                        onSelectPhoto={(photoFile) => {
                            setUploadedImage({ type: 'file', file: photoFile })
                        }}
                        onClearPhoto={() => {
                            setUploadedImage(null)
                        }}
                        selectedPhoto={uploadedImage}
                    />
                    <WorkflowPage.IconSection
                        title="Name (Required)"
                        icon={<Icon icon="calendarClock.large" background="round" />}
                    >
                        <TextField
                            isFullWidth
                            size="medium"
                            placeholder="Enter name"
                            value={scratchReservation?.name}
                            onClear={() => {
                                setScratchReservation((i) => i?.clone({ name: '' }))
                            }}
                            onChange={(e) => {
                                setScratchReservation((i) => i?.clone({ name: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title={
                            (scratchReservation?.duration?.toString() ?? '') === ''
                                ? scratchReservation.chargeBy === 'day'
                                    ? 'Price per day'
                                    : 'Price per night'
                                : 'Fixed price'
                        }
                        icon="dollar9.large"
                    >
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="0.00"
                            value={scratchReservation?.rate}
                            min={0}
                            step={0.01}
                            icon="dollar9.large"
                            onClear={() => {
                                setScratchReservation((i) => i?.clone({ rate: undefined }))
                            }}
                            onChange={(e) => {
                                setScratchReservation((i) => i?.clone({ rate: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Charge by" icon="calendar.large">
                        <Select
                            size="medium"
                            options={[
                                {
                                    id: 'day',
                                    title: 'Day',
                                    subtitle:
                                        'A reservation starting Monday and ending Tuesday would count as two days',
                                },
                                {
                                    id: 'night',
                                    title: 'Night',
                                    subtitle:
                                        'A reservation starting Monday and ending Tuesday would count as one night',
                                },
                            ]}
                            selectedOptionID={scratchReservation?.chargeBy ?? 'night'}
                            onSelectOption={(option) => {
                                setScratchReservation(
                                    scratchReservation.clone({
                                        charge_by:
                                            option?.id === undefined
                                                ? null
                                                : option.id === 'day'
                                                  ? 'day'
                                                  : 'night',
                                    })
                                )
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Deposit" icon="dollar3.large">
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="0.00"
                            value={scratchReservation?.deposit}
                            min={0}
                            step={0.01}
                            icon={'dollar9.large'}
                            onClear={() => {
                                setScratchReservation((i) => i?.clone({ deposit: undefined }))
                            }}
                            onChange={(e) => {
                                setScratchReservation((i) => i?.clone({ deposit: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {owner.allowDiscount && (
                        <WorkflowPage.IconSection
                            title="Discount rate"
                            icon={<Icon icon="discount.large" background="round" />}
                        >
                            <Select
                                size="medium"
                                selectedOptionID={scratchReservation?.discount?.toString()}
                                options={Transaction.discountRateStrings().map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                onSelectOption={(option) => {
                                    if (option) {
                                        setScratchReservation((i) =>
                                            i?.clone({ discount: option.id })
                                        )
                                    }
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}
                    {owner.allowSalesTax && (
                        <WorkflowPage.IconSection
                            title="Allow sales tax"
                            description="Automatically calculate & apply sales tax"
                            icon={<Icon icon="percentage2.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchReservation?.allowSalesTax}
                                    onChange={(e) =>
                                        setScratchReservation((i) =>
                                            i?.clone({ allow_salestax: e })
                                        )
                                    }
                                />
                            }
                        />
                    )}

                    <WorkflowPage.IconSection
                        title={scratchReservation.startDate ? 'Duration (required)' : 'Duration'}
                        icon="clock.large"
                        description="Select 'Flexible' if the length varies like a hotel reservation"
                    >
                        <Select
                            size="medium"
                            selectedOptionID={scratchReservation?.duration?.toString() ?? ''}
                            options={durationDayOptions.map((option) => ({
                                id: option.value ?? '',
                                title: option.label ?? 'Flexible',
                                subtitle:
                                    option.value === ''
                                        ? 'The duration of the reservation changes with each booking'
                                        : undefined,
                            }))}
                            onSelectOption={(option) => {
                                if (option) {
                                    setScratchReservation((i) =>
                                        i?.clone({
                                            duration: option.id === '' ? undefined : option.id,
                                        })
                                    )
                                }
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Description"
                        icon={<Icon icon="note.large" background="round" />}
                    >
                        <TextArea
                            size="medium"
                            placeholder="Enter description"
                            value={scratchReservation?.memo}
                            isAutoResizing
                            onClear={() => {
                                setScratchReservation((i) => i?.clone({ memo: '' }))
                            }}
                            onChange={(e) => {
                                setScratchReservation((i) => i?.clone({ memo: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {enableAllFields && (
                        <WorkflowPage.IconSection
                            title="Calendar color"
                            description="Color to be associated with this service on your calendar"
                            icon={<Icon icon="palette.large" background="round" />}
                        >
                            <CalendarColorSelect
                                type="reservation"
                                color={scratchReservation?.color ?? ''}
                                onSelect={(color) => {
                                    setScratchReservation((i) => i?.clone({ color }))
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Scheduling">
                    <WorkflowPage.IconSection
                        title="Capacity (required)"
                        icon={<Icon icon="hash.large" background="round" />}
                    >
                        <Select
                            size="medium"
                            selectedOptionID={scratchReservation?.capacity?.toString()}
                            options={capacityOptions.map((option) => ({
                                id: option.id ?? '',
                                title: option.label ?? '',
                            }))}
                            onSelectOption={(option) => {
                                if (option) {
                                    setScratchReservation((i) => i?.clone({ capacity: option.id }))
                                }
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Check-in (required)"
                        icon="calendarArrowLeft.large"
                        description="Set the check-in time, duration, and availability"
                        thirdLine={
                            checkinDescription !== undefined ? (
                                <div className="text-secondary-400 font-normal">
                                    {checkinDescription}
                                </div>
                            ) : undefined
                        }
                        secondaryView={
                            <Button
                                onClick={() => setActiveModal('check-in')}
                                intent="tertiaryLink"
                                className="mr-2 translate-x-3 hover:opacity-100"
                                size="small"
                            >
                                {scratchReservation.checkinType === null ? 'Add' : 'Edit'}
                            </Button>
                        }
                        hasHoverEffect
                    />

                    <WorkflowPage.IconSection
                        title="Check-out (required)"
                        icon="calendarArrowRight.large"
                        description="Set the check-out time, duration, and availability"
                        thirdLine={
                            checkoutDescription !== undefined ? (
                                <div className="text-secondary-400 font-normal">
                                    {checkoutDescription}
                                </div>
                            ) : undefined
                        }
                        secondaryView={
                            <Button
                                onClick={() => setActiveModal('check-out')}
                                intent="tertiaryLink"
                                className="mr-2 translate-x-3 hover:opacity-100"
                                size="small"
                            >
                                {scratchReservation.checkoutType === null ? 'Add' : 'Edit'}
                            </Button>
                        }
                        hasHoverEffect
                    />

                    <WorkflowPage.IconSection
                        title="Lead time"
                        description="Minimum amount of time needed to prep for this service"
                        icon={<Icon icon="stopwatch.large" background="round" />}
                    >
                        <Select
                            size="medium"
                            selectedOptionID={scratchReservation?.leadtime?.toString()}
                            options={Item.leadtimeStrings(scratchReservation?.type).map(
                                (option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                })
                            )}
                            onSelectOption={(option) => {
                                if (option) {
                                    setScratchReservation((i) => i?.clone({ leadtime: option.id }))
                                }
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="One-time event"
                        description="Enable for one-time events like camps"
                        icon={<Icon icon="windowClock.large" background="round" />}
                        hasHoverEffect
                        secondaryView={
                            <Checkbox
                                isChecked={hasFixedStartTime}
                                onChange={(e) => {
                                    setHasFixedStartTime(e)
                                    if (!e && !scratchReservation.schedule) {
                                        setScratchReservation((i) =>
                                            i?.clone({ schedule: owner.schedule })
                                        )
                                    }
                                }}
                            />
                        }
                    />

                    {hasFixedStartTime ? (
                        <WorkflowPage.IconSection
                            title="Start date"
                            icon={<div className="size-12"></div>}
                        >
                            <DatePicker
                                size="medium"
                                placeholder="Select date"
                                selectedDateString={scratchReservation?.startDate?.toISOString()}
                                onSelectDate={(date) => {
                                    setScratchReservation((i) =>
                                        i?.clone({ start_date: moment(date) })
                                    )
                                }}
                            />
                        </WorkflowPage.IconSection>
                    ) : (
                        <WorkflowPage.IconSection
                            title="Days of the week"
                            icon={<Icon icon="calendar.large" background="round" />}
                        >
                            <SelectDaysOfTheWeek
                                schedule={scratchReservation?.schedule ?? owner.schedule}
                                onChange={(schedule) => {
                                    setScratchReservation((i) => i?.clone({ schedule }))
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}

                    {owner.allowWidget && (
                        <WorkflowPage.IconSection
                            title="Show online"
                            description="Allow clients to purchase this service online"
                            icon={<Icon icon="computerClock.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchReservation?.isPublic}
                                    onChange={(e) => {
                                        setScratchReservation((i) => i?.clone({ is_public: e }))
                                    }}
                                />
                            }
                        />
                    )}
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Location">
                    <WorkflowPage.IconSection
                        title="Location"
                        icon={<Icon icon="markerPin.large" background="round" />}
                    >
                        <ItemLocationChips
                            item={scratchReservation}
                            onUpdateItem={setScratchReservation}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Attachments">
                    <WorkflowPage.IconSection
                        title="Attach a contract"
                        description="This contract will be signed when a client purchases this service"
                        icon={<Icon icon="pen.large" background="round" />}
                    >
                        <ContractSearchableSelect
                            selectedContract={scratchReservation.contract}
                            onSelectContract={(contract) => {
                                setScratchReservation((i) => i?.clone({ contract }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Attach a form"
                        description="This form will be completed when a client purchases this service"
                        icon={<Icon icon="field.large" background="round" />}
                    >
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={scratchReservation.form}
                            onSelectForm={(form) => {
                                setScratchReservation((i) => i?.clone({ form }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <ConfirmationModal
                    title="Would you like to continue without adding a price?"
                    isOpen={guardrail === 'price'}
                    onClose={() => setGuardrail(undefined)}
                    onConfirm={() => {
                        setScratchReservation((i) => i?.clone({ rate: 0 }))
                        setGuardrail(undefined)
                    }}
                >
                    <Paragraph className="text-gray-600">
                        If you don’t add a price this service will be shown as a price of “TBD” and
                        allow you to edit the price on a service by service basis.
                    </Paragraph>
                    <Paragraph className="mt-4 text-gray-600">
                        This is great if you don’t necessarily know the price of the service before
                        booking it.
                    </Paragraph>
                </ConfirmationModal>

                <ItemLocationModal
                    item={scratchReservation}
                    isOpen={guardrail === 'location'}
                    onClose={() => {
                        setGuardrail(undefined)
                    }}
                    onConfirm={(item) => {
                        setScratchReservation(item)
                        setGuardrail(undefined)
                    }}
                />

                <ConfirmationModal
                    title="Single capacity"
                    isOpen={guardrail === 'singleCapacity'}
                    onClose={() => setGuardrail(undefined)}
                    onConfirm={() => {
                        setScratchReservation((i) => i?.clone({ capacity: 1 }))
                        setGuardrail(undefined)
                    }}
                >
                    <Paragraph className="text-gray-600">
                        By toggling single capacity on this service can only be booked once.
                        Multiple clients will not be able to book this service in the same time
                        slot.
                    </Paragraph>
                </ConfirmationModal>

                <Modal
                    isOpen={activeModal === 'check-in'}
                    onClose={() => setActiveModal(null)}
                    title="Check-in"
                    footer={
                        <div className="flex items-center justify-end gap-x-4">
                            <Button
                                rightIcon={<Icon icon="externalLink.medium" />}
                                intent="secondary"
                                href="https://support.pocketsuite.io/hc/en-us/articles/360018779891-Reservations"
                                target="_blank"
                            >
                                Learn more
                            </Button>

                            <Button intent="primary" onClick={() => setActiveModal(null)}>
                                Done
                            </Button>
                        </div>
                    }
                >
                    <div className="flex flex-col items-stretch justify-start gap-y-8">
                        <WorkflowPage.IconSection
                            title="Check-in type"
                            icon="calendarArrowLeft.large"
                        >
                            <Select
                                size="medium"
                                options={[
                                    {
                                        id: 'flex',
                                        title: 'Flexible check-in',
                                        subtitle:
                                            'Schedule check-in appointments with a duration at different times for each client',
                                    },
                                    {
                                        id: 'any',
                                        title: 'Any time',
                                        subtitle:
                                            'Clients can check in at any time during available hours',
                                    },
                                    {
                                        id: 'fixed',
                                        title: 'Default check-in time',
                                        subtitle:
                                            'All clients check in at the same time regardless of other bookings',
                                    },
                                ]}
                                selectedOptionID={scratchReservation.checkinType ?? ''}
                                onSelectOption={(option) => {
                                    setScratchReservation(
                                        scratchReservation.clone({
                                            checkin_type:
                                                option.id === 'flex'
                                                    ? 'flex'
                                                    : option.id === 'any'
                                                      ? 'any'
                                                      : 'fixed',
                                        })
                                    )
                                }}
                            />
                        </WorkflowPage.IconSection>

                        {scratchReservation.checkinType === 'flex' && (
                            <WorkflowPage.IconSection
                                title="Check-in duration"
                                icon="stopwatch.large"
                            >
                                <SearchableSelect
                                    size="medium"
                                    items={[
                                        { id: '0', title: 'No duration', value: 0 },
                                        ...fiveMinuteIncrementOptions.map((option) => ({
                                            id: option.value,
                                            title: option.label,
                                            value: parseInt(option.value),
                                        })),
                                    ]}
                                    selectedItemID={
                                        scratchReservation.checkinDuration?.toString() ?? ''
                                    }
                                    onSelectItem={(item) => {
                                        if (item === null) {
                                            return
                                        }

                                        setScratchReservation(
                                            scratchReservation.clone({
                                                checkin_duration: item.value,
                                            })
                                        )
                                    }}
                                />
                            </WorkflowPage.IconSection>
                        )}

                        {scratchReservation.checkinType === 'fixed' && (
                            <WorkflowPage.IconSection
                                title="Default check-in time"
                                icon="clock.large"
                            >
                                <div className="grid grid-cols-[1fr_1fr_min-content] gap-x-2">
                                    <Select
                                        size="medium"
                                        options={times(12).map((hourIndex) => ({
                                            id: (hourIndex === 11 ? 0 : hourIndex + 1).toString(),
                                            title: (hourIndex + 1).toString(),
                                            value: hourIndex === 11 ? 0 : hourIndex + 1,
                                        }))}
                                        selectedOptionID={checkinTimeHour.toString()}
                                        onSelectOption={(option) =>
                                            setScratchReservation(
                                                scratchReservation.clone({
                                                    checkin_time:
                                                        option.value +
                                                        (checkinTimeAMPM === 'AM' ? 0 : 12) +
                                                        (checkinTimeMinute === 0 ? 0 : 0.5),
                                                })
                                            )
                                        }
                                    />
                                    <Select
                                        size="medium"
                                        options={[
                                            { id: '00', title: '00', value: 0 },
                                            { id: '30', title: '30', value: 30 },
                                        ]}
                                        selectedOptionID={checkinTimeMinute === 0 ? '00' : '30'}
                                        onSelectOption={(option) =>
                                            setScratchReservation(
                                                scratchReservation.clone({
                                                    checkin_time:
                                                        checkinTimeHour +
                                                        (checkinTimeAMPM === 'AM' ? 0 : 12) +
                                                        (option.value === 0 ? 0 : 0.5),
                                                })
                                            )
                                        }
                                    />
                                    <SegmentedControl
                                        segments={[
                                            { label: 'AM', value: 'AM' },
                                            { label: 'PM', value: 'PM' },
                                        ]}
                                        value={checkinTimeAMPM}
                                        onChange={(value) =>
                                            setScratchReservation(
                                                scratchReservation.clone({
                                                    checkin_time:
                                                        checkinTimeHour +
                                                        (value === 'AM' ? 0 : 12) +
                                                        (checkinTimeMinute === 0 ? 0 : 0.5),
                                                })
                                            )
                                        }
                                    />
                                </div>
                            </WorkflowPage.IconSection>
                        )}

                        {scratchReservation.checkinType !== null && (
                            <WorkflowPage.IconSection
                                title="Check-in availability"
                                icon="clock.large"
                                description="Setting reservation availability is optional, and (if entered) will override your main business hours when scheduling this specific reservation."
                            >
                                <div className="flex flex-col items-stretch justify-start gap-y-8">
                                    <SegmentedControl
                                        segments={[
                                            {
                                                label: 'Same as business',
                                                value: 'Same as business',
                                            },
                                            { label: 'Custom schedule', value: 'Custom schedule' },
                                        ]}
                                        value={checkinAvailabilityType}
                                        onChange={(value) => {
                                            if (value === 'Same as business') {
                                                setCheckinAvailabilityType('Same as business')
                                                handleAvailabilityChange(
                                                    availability.map((weekday) => ({
                                                        ...weekday,
                                                        enabled: false,
                                                    }))
                                                )
                                            }
                                            else {
                                                setCheckinAvailabilityType('Custom schedule')
                                            }
                                        }}
                                        className="w-full"
                                    />

                                    {checkinAvailabilityType === 'Custom schedule' && (
                                        <label className="relative flex cursor-pointer items-center justify-between gap-x-2">
                                            <div className="absolute -inset-4 rounded-lg hover:bg-black/4" />

                                            <div>
                                                <Label size="md">Custom schedule</Label>
                                                <Paragraph
                                                    size="sm"
                                                    className={
                                                        availability.some(
                                                            (weekday) => weekday.enabled
                                                        )
                                                            ? 'text-secondary-400'
                                                            : 'text-gray-600'
                                                    }
                                                >
                                                    {createAvailabilitySummary(
                                                        availability,
                                                        'Select custom schedule'
                                                    )}
                                                </Paragraph>
                                            </div>

                                            <Button
                                                onClick={() =>
                                                    setActiveModal('check-in-availability-hours')
                                                }
                                                intent="tertiaryLink"
                                                className="mr-2 translate-x-3 hover:opacity-100"
                                                size="small"
                                            >
                                                {availability.some((weekday) => weekday.enabled)
                                                    ? 'Edit'
                                                    : 'Select'}
                                            </Button>
                                        </label>
                                    )}
                                </div>
                            </WorkflowPage.IconSection>
                        )}
                    </div>
                </Modal>

                <SetupBusinessHoursModal
                    key={checkinAvailabilityType}
                    isOpen={activeModal === 'check-in-availability-hours'}
                    onClose={() => setActiveModal(null)}
                    onBack={() => setActiveModal('check-in')}
                    onChange={handleAvailabilityChange}
                    onDone={(newWeekdays) => {
                        setActiveModal('check-in')
                        handleAvailabilityChange(newWeekdays)
                    }}
                    description="Setting check-in availability is optional, and (if entered) will override your main business hours when scheduling this specific reservation."
                    currentAvailability={availability}
                    title="Check-in availability"
                    shouldHideTimePickers={scratchReservation.checkinType === 'fixed'}
                />

                <Modal
                    isOpen={activeModal === 'check-out'}
                    onClose={() => setActiveModal(null)}
                    title="Check-out"
                    footer={
                        <div className="flex items-center justify-end gap-x-4">
                            <Button
                                rightIcon={<Icon icon="externalLink.medium" />}
                                intent="secondary"
                                href="https://support.pocketsuite.io/hc/en-us/articles/360018779891-Reservations"
                                target="_blank"
                            >
                                Learn more
                            </Button>

                            <Button intent="primary" onClick={() => setActiveModal(null)}>
                                Done
                            </Button>
                        </div>
                    }
                >
                    <div className="flex flex-col items-stretch justify-start gap-y-8">
                        <WorkflowPage.IconSection
                            title="Check-out type"
                            icon="calendarArrowRight.large"
                        >
                            <Select
                                size="medium"
                                options={[
                                    {
                                        id: 'flex',
                                        title: 'Flexible check-out',
                                        subtitle:
                                            'Schedule check-out appointments with a duration at different times for each client',
                                    },
                                    {
                                        id: 'any',
                                        title: 'Any time',
                                        subtitle:
                                            'Clients can check out at any time during available hours',
                                    },
                                    {
                                        id: 'fixed',
                                        title: 'Default check-out time',
                                        subtitle:
                                            'All clients check out at the same time regardless of other bookings',
                                    },
                                ]}
                                selectedOptionID={scratchReservation.checkoutType ?? ''}
                                onSelectOption={(option) => {
                                    setScratchReservation(
                                        scratchReservation.clone({
                                            checkout_type:
                                                option.id === 'flex'
                                                    ? 'flex'
                                                    : option.id === 'any'
                                                      ? 'any'
                                                      : 'fixed',
                                        })
                                    )
                                }}
                            />
                        </WorkflowPage.IconSection>

                        {scratchReservation.checkoutType === 'flex' && (
                            <WorkflowPage.IconSection
                                title="Check-out duration"
                                icon="stopwatch.large"
                            >
                                <SearchableSelect
                                    size="medium"
                                    items={[
                                        { id: '0', title: 'No duration', value: 0 },
                                        ...fiveMinuteIncrementOptions.map((option) => ({
                                            id: option.value,
                                            title: option.label,
                                            value: parseInt(option.value),
                                        })),
                                    ]}
                                    selectedItemID={
                                        scratchReservation.checkoutDuration?.toString() ?? ''
                                    }
                                    onSelectItem={(item) => {
                                        if (item === null) {
                                            return
                                        }

                                        setScratchReservation(
                                            scratchReservation.clone({
                                                checkout_duration: item.value,
                                            })
                                        )
                                    }}
                                />
                            </WorkflowPage.IconSection>
                        )}

                        {(scratchReservation.checkoutType === 'flex' ||
                            scratchReservation.checkoutType === 'any') && (
                            <WorkflowPage.IconSection
                                title="Check-out availability"
                                icon="clock.large"
                                description="Settings reservation availability is optional, and (if entered) will override your main business hours when scheduling this specific service."
                            >
                                <SegmentedControl
                                    segments={[
                                        { label: 'Same as business', value: 'Same as business' },
                                        { label: 'Custom schedule', value: 'Custom schedule' },
                                    ]}
                                    value={
                                        isCheckoutAvailabilitySameAsBusiness
                                            ? 'Same as business'
                                            : 'Custom schedule'
                                    }
                                    onChange={(value) => {
                                        if (value === 'Same as business') {
                                            setScratchReservation(
                                                scratchReservation.clone({
                                                    checkout_start_time: null,
                                                    checkout_end_time: null,
                                                })
                                            )
                                        }
                                        else {
                                            setScratchReservation(
                                                scratchReservation.clone({
                                                    checkout_start_time: 9,
                                                    checkout_end_time: 9.5,
                                                })
                                            )
                                        }
                                    }}
                                    className="w-full"
                                />

                                {!isCheckoutAvailabilitySameAsBusiness &&
                                    scratchReservation.checkoutStartTime !== null &&
                                    scratchReservation.checkoutEndTime !== null && (
                                        <div className="mt-4 grid grid-cols-[1fr_min-content_1fr] items-center gap-x-2">
                                            <Select
                                                size="medium"
                                                options={halfHourTimeOptions}
                                                selectedOptionID={scratchReservation.checkoutStartTime.toString()}
                                                onSelectOption={(option) =>
                                                    setScratchReservation(
                                                        scratchReservation.clone({
                                                            checkout_start_time: option.value,
                                                            checkout_end_time:
                                                                (scratchReservation.checkoutEndTime ??
                                                                    0) < option.value
                                                                    ? option.value
                                                                    : scratchReservation.checkoutEndTime,
                                                        })
                                                    )
                                                }
                                            />
                                            <span>-</span>
                                            <Select
                                                size="medium"
                                                options={halfHourTimeOptions.filter(
                                                    (option) =>
                                                        option.value >=
                                                        (scratchReservation.checkoutStartTime ?? 0)
                                                )}
                                                selectedOptionID={scratchReservation.checkoutEndTime.toString()}
                                                onSelectOption={(option) =>
                                                    setScratchReservation(
                                                        scratchReservation.clone({
                                                            checkout_end_time: option.value,
                                                        })
                                                    )
                                                }
                                            />
                                        </div>
                                    )}
                            </WorkflowPage.IconSection>
                        )}

                        {scratchReservation.checkoutType === 'fixed' && (
                            <WorkflowPage.IconSection
                                title="Default check-out time"
                                icon="clock.large"
                            >
                                <div className="grid grid-cols-[1fr_1fr_min-content] gap-x-2">
                                    <Select
                                        size="medium"
                                        options={times(12).map((hourIndex) => ({
                                            id: (hourIndex === 11 ? 0 : hourIndex + 1).toString(),
                                            title: (hourIndex + 1).toString(),
                                            value: hourIndex === 11 ? 0 : hourIndex + 1,
                                        }))}
                                        selectedOptionID={checkoutTimeHour.toString()}
                                        onSelectOption={(option) =>
                                            setScratchReservation(
                                                scratchReservation.clone({
                                                    checkout_time:
                                                        option.value +
                                                        (checkoutTimeAMPM === 'AM' ? 0 : 12) +
                                                        (checkoutTimeMinute === 0 ? 0 : 0.5),
                                                })
                                            )
                                        }
                                    />
                                    <Select
                                        size="medium"
                                        options={[
                                            { id: '00', title: '00', value: 0 },
                                            { id: '30', title: '30', value: 30 },
                                        ]}
                                        selectedOptionID={checkoutTimeMinute === 0 ? '00' : '30'}
                                        onSelectOption={(option) =>
                                            setScratchReservation(
                                                scratchReservation.clone({
                                                    checkout_time:
                                                        checkoutTimeHour +
                                                        (checkoutTimeAMPM === 'AM' ? 0 : 12) +
                                                        (option.value === 0 ? 0 : 0.5),
                                                })
                                            )
                                        }
                                    />
                                    <SegmentedControl
                                        segments={[
                                            { label: 'AM', value: 'AM' },
                                            { label: 'PM', value: 'PM' },
                                        ]}
                                        value={checkoutTimeAMPM}
                                        onChange={(value) =>
                                            setScratchReservation(
                                                scratchReservation.clone({
                                                    checkout_time:
                                                        checkoutTimeHour +
                                                        (value === 'AM' ? 0 : 12) +
                                                        (checkoutTimeMinute === 0 ? 0 : 0.5),
                                                })
                                            )
                                        }
                                    />
                                </div>
                            </WorkflowPage.IconSection>
                        )}
                    </div>
                </Modal>
            </div>
        </WorkflowPage>
    )
}

const halfHourTimeOptions = times(48).map((index) => {
    const [hour, minute, ampm] = timeComponentsFromDecimalTime(index / 2)

    return {
        id: (index / 2).toString(),
        title: `${hour === 0 ? 12 : hour}:${minute.toString().padStart(2, '0')} ${ampm}`,
        value: index / 2,
    }
})
